/**
 *  @file       widgets/accounts-list/Balance.jsx
 *  @author     Jordan Cartlidge <jordan@kingsds.network>
 *  @author     Joash Mathew <joash@kingsds.network>
 * 
 *  @date       June 2022
 *  @date       September 2022
 * 
 *              A component which returns the balance text to a fixed decimal place.
 */

import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';

import './Balance.css';

const { VITE_ASSETS_PATH } = import.meta.env;

/** @typedef {import('bignumber.js').BigNumber} BigNumber */

/**
 * Returns the balance padded to a set number of decimal points
 *
 * @param   {object}      props           Component properties.
 * @param   {BigNumber}   props.balance   DCC balance.
 * @param   {string}      props.className Additional class names to style the component.
 * @returns {JSX.Element}                 DCC balance fixed to a set number of decimal points
 */
export function Balance({ balance, className, dark })
{
  const { i18n } = useTranslation();

  // Runtime check for a valid `BigNumber` value.
  if (!BigNumber.isBigNumber(balance) || balance.isNaN())
  {
    return <span>Unable to display credits</span>;
  }

  const balanceString = balance.toFixed(3);
  const [nonFractional, fractional] = balanceString.split('.');
  let formattedString = '';
  let creditsImgFileName = dark ? 'credits-grey.png' : 'credits-green.png';

  if (Number.isSafeInteger(Number(nonFractional)) || nonFractional.length <= 16)
    formattedString += `${new Intl.NumberFormat(i18n.language).format(nonFractional)}.${fractional}`;
  else
  {
    const prefix = nonFractional.slice(0, -16);
    const formattable = nonFractional.slice(-16, -1);
    const formatted = new Intl.NumberFormat(i18n.language, {
      minimumIntegerDigits: 16,
    }).format(formattable);

    formattedString += `${prefix}${formatted}.${fractional}`;
  }

  return (
    <span className={`dcc ${className ? className : ''} ${dark ? 'dark' : ''}`}>
      {formattedString}
      <img src={`${VITE_ASSETS_PATH}/img/${creditsImgFileName}`} alt="Credits icon" />
    </span>
  );
};
